import SettingContentWrapper from "@/modules/shared/SettingContentWrapper"
import SettingSection from "@/modules/shared/SettingSection"
import { useTranslation } from "react-i18next"
import React, { useContext, useEffect, useState } from "react"
import PlanCard from "./components/PlanCard"
import { getActiveSubscription, getBillingProducts } from "@/api/billing"
import { EnrichedPlan, Price, Subscription } from "@/types/billingPlans"
import { Loader } from "@/UI"
import { isEmpty } from "lodash"
import { useRecoilValue } from "recoil"
import { userAtom } from "@/atoms/userAtom"
import SubscriptionSummary from "./components/SubscriptionSummary"
import { SandboxContext } from "@/layout/Nav/SandboxSwitcher"
import { CURRENCIES } from "@/constants/currencies"
import { currencyAtom } from "@/atoms/currencyAtom"
import { getSubscriptionCurrencySymbol } from "@/utils/currencies"
import { customOptimizePlan } from "./fixtures/customOptimizePlan"

interface PlansSettingsProps {}

const PlansSettings: React.FC<PlansSettingsProps> = () => {
  const sandbox = useContext(SandboxContext)

  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [plans, setPlans] = useState<EnrichedPlan[]>([])
  const [activePrice, setActivePrice] = useState<Price | undefined>()
  const [activeSubscription, setActiveSubscription] = useState<Subscription | undefined>()
  const user = useRecoilValue(userAtom)
  const currency = useRecoilValue(currencyAtom)

  const planIcons = [
    "/assets/images/plansSettings/basic.svg",
    "/assets/images/plansSettings/launch.svg",
    "/assets/images/plansSettings/grow.svg",
    "/assets/images/plansSettings/scale.svg",
  ]

  const stripeCountryKey =
    currency.code === CURRENCIES.EUR.code ? CURRENCIES.EUR.code : user.org.country

  const currencyToUse = getSubscriptionCurrencySymbol(user.org.country)
  const fetchActiveSubscription = async () => {
    try {
      const { data } = await getActiveSubscription()
      setActiveSubscription(data)

      const countryPrice = data?.product.prices[stripeCountryKey] || data?.product?.price

      setActivePrice(countryPrice)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchPlans = async () => {
    try {
      const { data } = await getBillingProducts()
      const enrichedPlans = data?.items
        .sort((a, b) => a.price.amount - b.price.amount)
        .map((plan, index) => ({
          ...plan,
          icon: planIcons[index] ? planIcons[index]! : planIcons[0]!,
        }))

      setPlans([...enrichedPlans, customOptimizePlan])
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setActiveSubscription(undefined)
    setIsLoading(true)
    fetchPlans()
    fetchActiveSubscription()
  }, [user?.org.id, sandbox.enabled])

  useEffect(() => {
    fetchActiveSubscription()
  }, [user.org.hasActiveSubscription])

  return (
    <SettingSection title={t("profile.PlansSettings.plansAndPricing")}>
      <SettingContentWrapper>
        {isLoading ? (
          <Loader fontSize={48} />
        ) : (
          <div className="flex flex-col space-y-4 xl:space-y-10 2xl:space-y-10">
            <SubscriptionSummary
              subscription={activeSubscription}
              price={activePrice}
              currency={currencyToUse}
            />
            {!isEmpty(plans) && (
              <div className="flex w-full flex-wrap gap-x-4 xl:gap-x-10 2xl:gap-x-14 gap-y-2">
                {plans?.map((plan) => (
                  <div className="basis-1 grow" key={plan.name}>
                    <PlanCard
                      isActive={plan.id === activeSubscription?.product?.id}
                      cancelAtPeriodEnd={activeSubscription?.cancelAtPeriodEnd || false}
                      plan={plan}
                      activePrice={plan?.prices[stripeCountryKey] || plan.price}
                      isUpgrade={
                        (activeSubscription?.product?.price?.amount || 0) < plan?.price?.amount
                      }
                      currency={currencyToUse}
                    />
                  </div>
                ))}
              </div>
            )}
            {/* <EnterpriseSection /> */}
          </div>
        )}
      </SettingContentWrapper>
    </SettingSection>
  )
}

export default PlansSettings
